import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Star, MapPin, Calendar, Phone, Globe, FileText, CheckCircle,
  Shield, Truck, MessageCircle, AlertTriangle, Facebook, Instagram, Twitter
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip"
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StorePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { shippingAddress } = useCart();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStore = useCallback(async () => {
    try {
      // The backend handles both ID and Slug on the same endpoint /stores/{id_or_slug}
      const endpoint = `${API_URL}/stores/${slug}`;

      const storeRes = await axios.get(endpoint);
      setStore(storeRes.data);

      let productUrl = `${API_URL}/products?store_id=${storeRes.data.id}`;

      const defaultAddr = shippingAddress || (user && user.saved_addresses ? (user.saved_addresses.find(a => a.id === user.default_address_id) || user.saved_addresses[0]) : null);
      if (defaultAddr && defaultAddr.coordinates) {
        const lat = defaultAddr.coordinates.lat;
        const lon = defaultAddr.coordinates.lon || defaultAddr.coordinates.lng;
        if (lat && lon) {
          productUrl += `&user_lat=${lat}&user_lon=${lon}`;
        }
      }

      const productsRes = await axios.get(productUrl);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching store:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Chargement...</div>;
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">Boutique non trouvée</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Banner */}
      <div
        className="h-48 md:h-64 bg-slate-900 relative"
        style={
          store.banner_url
            ? {
              backgroundImage: `url(${store.banner_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
            : {}
        }
      >
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header Content */}
        <div className="-mt-16 relative z-10 mb-8">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Logo */}
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-32 h-32 rounded-xl bg-white border-4 border-white shadow-sm overflow-hidden">
                    {store.logo_url ? (
                      <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-4xl font-bold text-gray-400">
                        {store.name.charAt(0)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 text-center md:text-left space-y-3">
                  <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 justify-center md:justify-start">
                        <h1 className="text-3xl font-bold text-gray-900">{store.name}</h1>
                        {store.is_verified && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <CheckCircle className="h-6 w-6 text-blue-500 fill-blue-50" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Boutique vérifiée : Identité et documents validés.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-4 justify-center md:justify-start">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Membre depuis {format(new Date(store.created_at), "MMMM yyyy", { locale: fr })}
                        </span>
                        {store.legal_docs_url && (
                          <a
                            href={`${BACKEND_URL}${store.legal_docs_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-green-700 hover:underline cursor-pointer"
                            title="Voir les documents légaux"
                          >
                            <FileText className="h-4 w-4" />
                            Enregistré légalement (Voir Docs)
                          </a>
                        )}
                      </div>
                    </div>

                    <Button
                      className="bg-orange-500 hover:bg-orange-600 gap-2"
                      onClick={() => {
                        if (!user) { navigate("/auth"); return; }
                        if (store.owner_id === user.id) return;
                        sessionStorage.setItem("msg_pending_contact", JSON.stringify({ id: store.owner_id, name: store.name }));
                        navigate("/messages");
                      }}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Contacter
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-center md:justify-start mt-2">
                    {store.store_type === 'SELLER' && <Badge variant="secondary">Vendeur</Badge>}
                    {store.store_type === 'SUPPLIER' && <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">Fournisseur</Badge>}
                    {store.store_type === 'ARTISAN' && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">Artisan</Badge>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mt-4 max-w-2xl">
                    {store.address && (
                      <div className="flex items-center gap-2 justify-center md:justify-start">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {store.address}
                      </div>
                    )}
                    {store.social_links && (
                      <div className="flex items-center gap-3 justify-center md:justify-start">
                        {store.social_links.facebook && <Facebook className="h-4 w-4 text-blue-700 cursor-pointer" />}
                        {store.social_links.instagram && <Instagram className="h-4 w-4 text-pink-600 cursor-pointer" />}
                        {store.social_links.twitter && <Twitter className="h-4 w-4 text-blue-400 cursor-pointer" />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="w-full justify-start bg-white p-1 rounded-lg border">
            <TabsTrigger value="products" className="flex-1 md:flex-none">Produits</TabsTrigger>
            <TabsTrigger value="about" className="flex-1 md:flex-none">À propos</TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1 md:flex-none">Avis</TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center bg-white">
                <p className="text-gray-500">Cette boutique n'a pas encore publié de produits.</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="about">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4">À propos de {store.name}</h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {store.description || "Aucune description fournie."}
                </p>

                {store.legal_docs_url && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Documents Légaux
                    </h4>
                    <a
                      href={`${BACKEND_URL}${store.legal_docs_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1"
                    >
                      Voir les documents de l'entreprise
                      <ArrowRight className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                Section avis en construction
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

// Add missing icon
import { ArrowRight } from "lucide-react";
